import { useState } from "react";
import { Send, Check, AlertCircle } from "lucide-react";
import api from "@/lib/api";

const INTERESTS = [
  "Private Lessons",
  "Junior Programs",
  "Adult Clinics",
  "Match Play",
  "Membership Information",
  "College Consulting",
  "Other",
];

const LOCATIONS = [
  { value: "baltimore", label: "Baltimore" },
  { value: "princeton", label: "Princeton — NJSC" },
  { value: "both", label: "Either Location" },
];

const inputCls =
  "w-full bg-[#090B0C] border border-[#1D1F21] text-[#F2EDE4] placeholder-[#3A3D40] px-4 py-3 text-sm focus:outline-none focus:border-[#0D6B56] transition-colors duration-400";
const labelCls = "block text-[10px] uppercase tracking-[0.15em] text-[#6B6E71] mb-2";

export const InquiryForm = ({ location, testId = "inquiry-form" }) => {
  const empty = { name: "", email: "", phone: "", subject: "", message: "", location: location || "" };
  const [form, setForm] = useState(empty);
  const [status, setStatus] = useState("idle");
  const [error, setError] = useState("");

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    setStatus("loading");
    setError("");
    try {
      await api.submitContact(form);
      setStatus("done");
    } catch (err) {
      setError(err.response?.data?.detail || "Something went wrong. Please try again.");
      setStatus("idle");
    }
  };

  if (status === "done") {
    return (
      <div className="text-center py-12" data-testid={`${testId}-success`}>
        <div className="w-12 h-12 border border-[#0D6B56] flex items-center justify-center mx-auto mb-6">
          <Check className="w-5 h-5 text-[#C8F542]" />
        </div>
        <h3 className="font-editorial text-2xl text-[#F2EDE4] mb-2">Inquiry Received</h3>
        <p className="text-xs text-[#6B6E71] mb-6">Our manager will respond within 24–48 hours.</p>
        <button onClick={() => { setForm(empty); setStatus("idle"); }} className="text-xs text-[#0D6B56] hover:text-[#C8F542] transition-colors" data-testid={`${testId}-reset`}>
          Send another inquiry
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={onSubmit} className="space-y-5" data-testid={testId}>
      {error && (
        <div className="border border-red-900/60 bg-red-950/30 text-red-300 px-4 py-3 text-xs flex items-center gap-2" data-testid={`${testId}-error`}>
          <AlertCircle className="w-4 h-4" /> {error}
        </div>
      )}
      <div className="grid sm:grid-cols-2 gap-5">
        <div>
          <label className={labelCls}>Full Name *</label>
          <input name="name" value={form.name} onChange={onChange} required className={inputCls} placeholder="Your name" data-testid={`${testId}-name`} />
        </div>
        <div>
          <label className={labelCls}>Email *</label>
          <input type="email" name="email" value={form.email} onChange={onChange} required className={inputCls} placeholder="you@example.com" data-testid={`${testId}-email`} />
        </div>
      </div>
      <div className="grid sm:grid-cols-2 gap-5">
        <div>
          <label className={labelCls}>Phone</label>
          <input type="tel" name="phone" value={form.phone} onChange={onChange} className={inputCls} placeholder="Optional" data-testid={`${testId}-phone`} />
        </div>
        <div>
          <label className={labelCls}>Interest *</label>
          <select name="subject" value={form.subject} onChange={onChange} required className={inputCls} data-testid={`${testId}-subject`}>
            <option value="">Select an interest</option>
            {INTERESTS.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
      </div>
      {!location && (
        <div>
          <label className={labelCls}>Location</label>
          <select name="location" value={form.location} onChange={onChange} className={inputCls} data-testid={`${testId}-location`}>
            <option value="">Select a location</option>
            {LOCATIONS.map((l) => <option key={l.value} value={l.value}>{l.label}</option>)}
          </select>
        </div>
      )}
      <div>
        <label className={labelCls}>Message *</label>
        <textarea name="message" value={form.message} onChange={onChange} required rows={4} className={`${inputCls} resize-none`} placeholder="Tell us about your goals, experience level, or questions" data-testid={`${testId}-message`} />
      </div>
      <button type="submit" disabled={status === "loading"} className="btn-primary inline-flex items-center gap-2 disabled:opacity-60" data-testid={`${testId}-submit`}>
        {status === "loading" ? "Sending…" : <><Send className="w-3.5 h-3.5" /> Send Inquiry</>}
      </button>
    </form>
  );
};

export default InquiryForm;
